import {defineStore} from 'pinia';
import Cookies from 'js-cookie';
import Crypto from 'crypto-js';
import authModule from './modules/AuthStore/index';
import layoutModule from './modules/LayoutStore/index';
import lookupsModule from './modules/LookupsStore/index';
import ordersModule from './modules/OrdersStore/index';
import {debugf, replaceAll} from "@helpers/string";

let customStorage = (function () {
    const COOKIE_KEY = 'sellers-shield-ssp';

    let localStorageSupport = Boolean(window.localStorage);

    if (!window.tokenSSPA) {
        //generate token based on client info - ugly, but we need to have an idea of who is playing with our ... app
        window.tokenSSPA = (() => {
            let generateToken = () => {
                    let YMD = (new Date()).toISOString().slice(0, 10),
                        infos = JSON.stringify(window.ModernizrInfo || {info: 'missing-Modernizr-info'}),
                        SESSION_HASH = window.env.SESSION_HASH || 'missing';

                    return [
                        replaceAll(`KEY${YMD}`, '-', ''),
                        SESSION_HASH,
                        Crypto.MD5(infos).toString(),
                    ].join('-');
                },
                savedHash = (Cookies.get(COOKIE_KEY) || '').split('/')[0],
                vuexToken = savedHash || generateToken();

            if (savedHash !== vuexToken) {
                let modernizrUnsupported = (() => {
                    let unsupported = window.ModernizrInfo?.features?.unsupported;
                    return Boolean(unsupported)
                        ? ['polyfill', unsupported]
                        : ['no-polyfill'];
                })();
                Cookies.set(
                    COOKIE_KEY,
                    [vuexToken, ...modernizrUnsupported].join('/'),
                    {expires: 7, sameSite: 'Lax'}
                );
            }

            return vuexToken;
        })();
    }

    return (STORAGE_KEY) => ({
        getItem: (key) => {
            let data = null;

            try {
                data = localStorageSupport
                    ? window.localStorage.getItem(STORAGE_KEY) || null
                    : Cookies.get(STORAGE_KEY) || null;

                if (data) {
                    // Decrypt the store retrieved from local storage using our encryption token stored in cookies.
                    const bytes = Crypto.AES.decrypt(`${data}`, window.tokenSSPA);

                    data = bytes.toString(Crypto.enc.Utf8)
                        .replace('\"_sspToken\":null', `\"_sspToken\":\"${window.tokenSSPA}\"`);

                    //simple test json - if it fails we don't use
                    JSON.parse(data);
                }
            } catch (e) {
                try {
                    // The store will be reset if decryption fails.
                    localStorageSupport
                        ? window.localStorage.removeItem(STORAGE_KEY)
                        : Cookies.remove(STORAGE_KEY);
                } catch (e) {
                    console.log(e)
                }
                data = null;
            }

            if (!data || data.indexOf('_sspToken') < 1) {
                data = JSON.stringify({
                    _sspToken: window.tokenSSPA
                });
            }

            debugf([`store--read #localStorage`], data);

            return data;
        },
        setItem: (key, state) => {
            try {
                let data = {...(JSON.parse(state) || {})},
                    meUuid = data?._me?.uuid || data?._me?._uuid;

                debugf(
                    [`store--write-persist`],
                    {data: Boolean(data), logged: Boolean(data?._accessToken)}
                );

                data._sspToken = null;
                data._thirdPartyPlatformList = null;

                if (Boolean(meUuid)) {
                    //remove anything that isn't required from datasets
                    data._me = meUuid ? {uuid: meUuid} : {};
                }

                // Encrypt the store using our encryption token stored in cookies.
                data = Crypto.AES.encrypt(JSON.stringify(data), window.tokenSSPA).toString();

                // Save the encrypted store in local storage.
                return localStorageSupport
                    ? window.localStorage.setItem(STORAGE_KEY, data)
                    : Cookies.set(STORAGE_KEY, data, {expires: 3});
            } catch (e) {
                return false;
            }
        }
    });
})();

export const useAuthStore = defineStore('auth', {
    ...authModule,
    persist: {
        enabled: true,
        strategies: [
            {
                storage: customStorage('sellers-shield-dt'),
            },
        ],
    },
    share: {
        // Override global config for this storeAuth.
        enable: true,
        initialize: false,
    },
});

export const useLayoutStore = defineStore('layout', {
    ...layoutModule
});

export const usePaymentStore = defineStore('payment', {
    state: () => ({
        completed: false
    })
});

export const useOrdersStore = defineStore('orders', {
    ...ordersModule,
});

export const useLookupsStore = defineStore('lookups', {
    ...lookupsModule,
});

export const vuexSspToken = window.tokenSSPA;